/**
 * LyricSynthesisDisplay — Merged lyrics with attribution chips and metadata.
 */

import React, { useState } from 'react';
import { Crown, Copy, Check, ChevronDown } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import { LYRIC_WRITER_AGENTS } from '@/data/lyricOrchestratorAgents';
import { copyToClipboard } from '@/utils/clipboard';
import type { LyricSynthesis } from '@/types/lyricOrchestrator.types';

interface LyricSynthesisDisplayProps {
  synthesis: LyricSynthesis;
  onAccept: () => void;
}

export const LyricSynthesisDisplay: React.FC<LyricSynthesisDisplayProps> = ({ synthesis, onAccept }) => {
  const [copied, setCopied] = useState(false);
  const [showRationale, setShowRationale] = useState(false);

  const handleCopy = async () => {
    try {
      await copyToClipboard(synthesis.mergedLyrics);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const agentMap = new Map(LYRIC_WRITER_AGENTS.map(a => [a.id, a]));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown size={18} className="text-amber-400" />
          <h3 className="text-sm font-black text-slate-200 uppercase tracking-wider">
            Parliament Write — Merged Lyrics
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            leftIcon={copied ? <Check size={14} /> : <Copy size={14} />}
            className="text-slate-400"
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onAccept}
          >
            Accept Lyrics
          </Button>
        </div>
      </div>

      {/* Title + Summary */}
      <div className="bg-slate-900/60 rounded-lg p-4 border border-slate-800">
        <h4 className="text-lg font-black text-amber-400">{synthesis.title}</h4>
        {synthesis.summary && (
          <p className="text-[12px] text-slate-400 mt-1 italic">{synthesis.summary}</p>
        )}
        {synthesis.styleTags && (
          <div className="flex flex-wrap gap-1 mt-2">
            {synthesis.styleTags.split(',').map((tag, i) => (
              <Badge key={i} variant="default-muted" size="xs">{tag.trim()}</Badge>
            ))}
          </div>
        )}
      </div>

      {/* Merged Lyrics */}
      <div className="bg-slate-950/50 rounded-lg p-4 border border-slate-800">
        <pre className="text-[12px] text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">
          {synthesis.mergedLyrics}
        </pre>
      </div>

      {/* Attribution chips */}
      {synthesis.attribution.length > 0 && (
        <div>
          <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
            Section Attribution
          </h5>
          <div className="flex flex-wrap gap-2">
            {synthesis.attribution.map((attr, i) => {
              const agent = agentMap.get(attr.sourceAgentId);
              const colorClass = agent?.color || 'text-slate-400';
              return (
                <div
                  key={i}
                  className="flex items-center gap-1.5 bg-slate-900/60 rounded-md px-2 py-1 border border-slate-800"
                >
                  <span className="text-[10px] font-bold text-slate-300">{attr.section}</span>
                  <span className="text-slate-700">&larr;</span>
                  <span className={`text-[10px] font-bold ${colorClass}`}>
                    {agent?.name || attr.sourceAgentId}
                  </span>
                  {attr.modified && (
                    <Badge variant="warning-muted" size="xs">edited</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Overall Assessment */}
      {synthesis.overallAssessment && (
        <div className="border-l-4 border-amber-500/50 pl-3 py-2">
          <p className="text-[12px] text-slate-300 italic">{synthesis.overallAssessment}</p>
        </div>
      )}

      {/* Merge Rationale (collapsible) */}
      {synthesis.mergeRationale && (
        <div>
          <button
            onClick={() => setShowRationale(!showRationale)}
            className="flex items-center gap-1 text-[10px] font-black text-slate-500 uppercase tracking-widest hover:text-slate-400 transition-colors"
          >
            <ChevronDown
              size={12}
              className={`transition-transform duration-200 ${showRationale ? 'rotate-180' : ''}`}
            />
            Merge Rationale
          </button>
          {showRationale && (
            <p className="text-[11px] text-slate-400 mt-1 bg-slate-900/40 rounded p-2 animate-in slide-in-from-top-2 duration-200">
              {synthesis.mergeRationale}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
